import { useIsomorphicLayoutEffect } from '@react-hookz/web';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { NsfwReason } from '~features/annals/enums/nsfwReason';
import { useSession } from '~hooks/useSession';
import { isSafeApp } from '~shared/utils/host';
import { getIsSafeMode } from '~store/auth/authSlice';
import {
	getIsModalShownForDemo,
	getIsModalShownForRegistered,
	safeModeModalActions,
} from '~store/safe-mode-modal';

import { useSafeModeRedirect } from './useSafeModeRedirect';
import {
	useSwitchSafeMode,
	useSwitchSafeModeWithAgeValidation,
} from './useSwitchSafeMode';

export const usePresetSafeMode = () => {
	const { isSuccess, isDemoUser, userId } = useSession();
	const isSafeModeUser = useSelector(getIsSafeMode);
	const modalShownForRegistered = useSelector(getIsModalShownForRegistered);
	const modalShownForDemo = useSelector(getIsModalShownForDemo);
	const dispatch = useDispatch();

	const updateSafeMode = useSwitchSafeModeWithAgeValidation();
	const switchSafeMode = useSwitchSafeMode();
	const { redirectToSFWDomain } = useSafeModeRedirect();

	const syncSafeMode = useCallback(() => {
		if (!isSafeApp && isSafeModeUser && modalShownForRegistered) {
			void switchSafeMode(false, { nsfwReason: NsfwReason.Grid });
		}

		if (isSafeApp && !isSafeModeUser) {
			void switchSafeMode(true, { nsfwReason: NsfwReason.Grid });
		}
	}, [isSafeModeUser, modalShownForRegistered, switchSafeMode]);

	const handleNSFWModal = useCallback(() => {
		if (isDemoUser) {
			if (!modalShownForDemo) {
				void updateSafeMode(false, { force: true, nsfwReason: NsfwReason.Grid })
					.then(() => dispatch(safeModeModalActions.showForDemo()))
					.catch(redirectToSFWDomain);
			}
			return;
		}

		if (!modalShownForRegistered && isSafeModeUser) {
			void updateSafeMode(false, { force: true, nsfwReason: NsfwReason.Grid })
				.then(() => dispatch(safeModeModalActions.showForRegistered()))
				.catch(redirectToSFWDomain);
		}
	}, [
		isDemoUser,
		modalShownForRegistered,
		isSafeModeUser,
		modalShownForDemo,
		updateSafeMode,
		redirectToSFWDomain,
		dispatch,
	]);

	useIsomorphicLayoutEffect(() => {
		if (isSuccess) {
			if (!isDemoUser) {
				syncSafeMode();
			}

			if (!isSafeApp) {
				handleNSFWModal();
			}
		}
	}, [isSuccess, userId]);
};
